import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Typography,
} from '@mui/material';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../app/store/configureStore';
import { useTranslation } from 'react-i18next';
import {
  clearKitchenDetails,
  fetchKitchenDetailsAsync,
} from './kitchenDetailsSlice';
import { ChevronRight, Shop2Outlined } from '@mui/icons-material';
import LoadingComponent from '../../../app/layout/LoadingComponent';
import ListTitleBox from '../../../app/components/list/ListTitleBox';
import StyledTableRow from '../../../app/components/StyledTableRow';
import KitchenMealItems from './KitchenMealItems';
import i18n from '../../../i18n';
import ErrorComponent from '../../../app/layout/ErrorComponent';

export default function KitchenDetails() {
  const { restaurantId } = useParams<{
    restaurantId: string;
  }>();
  const { kitchenId } = useParams<{ kitchenId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { kitchen, status } = useAppSelector((state) => state.kitchen);

  const header = (
    <ListTitleBox
      title={t('kitchen.details.title')}
      onBackClick={() => navigate(-1)}
    />
  );

  const fetchKitchenDetails = async () => {
    dispatch(clearKitchenDetails());
    dispatch(fetchKitchenDetailsAsync({ restaurantId, kitchenId }));
  };

  useEffect(() => {
    fetchKitchenDetails();
  }, [kitchenId, dispatch, restaurantId]);

  if (status.includes('pending')) return <LoadingComponent header={header} />;

  if (!kitchen)
    return (
      <ErrorComponent
        header={header}
        title={t('errors.generic.title')}
        description={t('errors.generic.description')}
        primaryButtonText={t('common.retry')}
        secondaryButtonText={t('common.back')}
        onPrimaryAction={() => fetchKitchenDetails()}
      />
    );

  const kitchenName =
    kitchen.name.languages.find(
      (language) => language.languageCode === i18n.language
    )?.value ||
    kitchen.name.languages.find((language) => language.languageCode === 'en-US')
      ?.value;

  return (
    <>
      <ListTitleBox title={kitchenName} onBackClick={() => navigate(-1)} />
      <TableContainer
        component={Paper}
        sx={{
          width: '100%',
          mb: 1,
          px: 0,
          '& tr:last-child td': { borderBottom: 'none' },
        }}
      >
        <Table>
          <TableBody>
            <StyledTableRow
              onClick={() =>
                navigate(
                  `orders?kitchenName=${kitchenName}&showBackButton=true`
                )
              }
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Shop2Outlined color="primary" />
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {t('kitchen.details.links.orders')}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell
                align="right"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <ChevronRight sx={{ color: 'text.secondary' }} />
              </TableCell>
            </StyledTableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <KitchenMealItems />
    </>
  );
}
